import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';

interface NotificationDropdownProps {
  onClose: () => void;
}

interface Notification {
  userId: string;
  notificationId: string;
  type: string;
  photoId: string;
  commentId?: string;
  actorUserId: string;
  actorName: string;
  title: string;
  body: string;
  emoji?: string;
  read: string;
  createdAt: string;
}

export default function NotificationDropdown({ onClose }: NotificationDropdownProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch recent notifications with aggressive polling for real-time updates
  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ['notifications-recent'],
    queryFn: () => api.listNotifications({ limit: 10 }),
    refetchInterval: 10 * 1000, // Poll every 10 seconds while dropdown is open
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true, // Refetch when dropdown opens
    refetchOnWindowFocus: true, // Refetch when window gains focus
  });

  const notifications: Notification[] = notificationsData?.notifications || [];

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) => api.markNotificationAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-recent'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: () => api.markAllNotificationsAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-recent'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (notification.read === 'false') {
      await markAsReadMutation.mutateAsync(notification.notificationId);
    }

    // Navigate to the photo
    onClose();
    navigate(`/feed?photoId=${notification.photoId}`);
  };

  const formatTimeAgo = (dateString: string): string => {
    const now = new Date();
    const date = new Date(dateString);
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getNotificationIcon = (type: string, emoji?: string): string => {
    if (emoji) return emoji;
    switch (type) {
      case 'comment_reaction': return '❤️';
      case 'tag_mention': return '🏷️';
      case 'new_comment': return '💬';
      case 'new_audio': return '🎤';
      case 'photo_share': return '🔗';
      default: return '🔔';
    }
  };

  return (
    <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-[100] max-h-[80vh] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="text-lg font-bold text-gray-900">Notifications</h3>
        {notifications.some(n => n.read === 'false') && (
          <button
            onClick={() => markAllAsReadMutation.mutate()}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
            disabled={markAllAsReadMutation.isPending}
          >
            {markAllAsReadMutation.isPending ? 'Marking...' : 'Mark all read'}
          </button>
        )}
      </div>

      {/* Notifications List */}
      <div className="overflow-y-auto flex-1">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mx-auto mb-2"></div>
            <p className="text-sm">Loading...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <div className="text-5xl mb-3">🔔</div>
            <p className="text-sm font-medium">No notifications yet</p>
            <p className="text-xs text-gray-400 mt-1">
              You'll see notifications here when someone reacts to your comments or tags you
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((notification) => {
              const isUnread = notification.read === 'false';
              return (
                <button
                  key={notification.notificationId}
                  onClick={() => handleNotificationClick(notification)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                    isUnread ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className="text-2xl flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notification.type, notification.emoji)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 mb-0.5">
                        {notification.title}
                      </p>
                      {notification.body && (
                        <p className="text-xs text-gray-600 line-clamp-2">
                          {notification.body}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {formatTimeAgo(notification.createdAt)}
                      </p>
                    </div>

                    {/* Unread indicator */}
                    {isUnread && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2"></div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="border-t border-gray-200 px-4 py-3">
          <button
            onClick={() => {
              onClose();
              navigate('/notifications');
            }}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium w-full text-center"
          >
            See all notifications →
          </button>
        </div>
      )}
    </div>
  );
}

